import { BrandFeeConfig, FeeConfigSummary, InspectionVariableFeeTemplate, VariableFeeRow } from '../../../types/VariableFee'
import { getDefaultFeeRows } from '../constants'
import { mergeActive, mergeAmounts } from './mergeConflict'
import { buildSiteLabelGroups, groupBy } from './overviewUtils'

// Part du catalogue de lignes par défaut (une par famille/localisation/position/type de frais)
// et applique, pour chaque ligne, la valeur trouvée dans `data` si elle existe — sinon la ligne
// reste inchangée (catalogue par défaut = amount null, inactif). S'appelle en cascade pour
// empiler plusieurs replis (ex : global, puis site par-dessus).
export function applyDataToRows(
    defaultRows: VariableFeeRow[],
    data: InspectionVariableFeeTemplate[],
): VariableFeeRow[] {
    return defaultRows.map(row => {
        const match = data.find(d =>
            Number(d.family) === row.family &&
            d.location === row.location &&
            d.position === row.position &&
            d.fee_type === row.feeType,
        )

        return match
            ? { ...row, isActive: match.is_active, averageAmount: match.amount !== null ? match.amount / 100 : null }
            : row
    })
}

// Ligne globale correspondant à une ligne affichée, pour montrer "valeur globale (référence)"
// sous chaque champ quand on édite un site.
export function getGlobalRow(
    globalData: InspectionVariableFeeTemplate[],
    row: VariableFeeRow,
): InspectionVariableFeeTemplate | undefined {
    return globalData.find(d =>
        Number(d.family) === row.family &&
        d.location === row.location &&
        d.position === row.position &&
        d.fee_type === row.feeType,
    )
}

export function rowKey(row: { family: VariableFeeRow['family'], location: VariableFeeRow['location'], position: VariableFeeRow['position'], feeType: VariableFeeRow['feeType'] }): string {
    return `${row.family}|${row.location}|${row.position ?? ''}|${row.feeType}`
}

/**
 * Valeur par défaut quand plusieurs sites ou plusieurs marques sont sélectionnés et ont des
 * valeurs divergentes déjà en base : montant le plus élevé, actif dès qu'au moins une entité
 * l'est. Chaque type de conflit (montant / actif) est signalé indépendamment, une ligne pouvant
 * diverger sur l'un, l'autre, ou les deux.
 */
export function mergeRowsAcrossEntities(
    globalData: InspectionVariableFeeTemplate[],
    entitiesData: InspectionVariableFeeTemplate[][],
): { rows: VariableFeeRow[], amountConflictKeys: Set<string>, activeConflictKeys: Set<string> } {
    const amountConflictKeys = new Set<string>()
    const activeConflictKeys = new Set<string>()

    const rows = getDefaultFeeRows().map(catalogRow => {
        const effectiveRows = entitiesData.map(entityData =>
            applyDataToRows(applyDataToRows([ catalogRow ], globalData), entityData)[0],
        )

        const { value: averageAmount, hasConflict: amountConflict } = mergeAmounts(effectiveRows.map(r => r.averageAmount))
        const { value: isActive, hasConflict: activeConflict } = mergeActive(effectiveRows.map(r => r.isActive))

        if (amountConflict) {
            amountConflictKeys.add(rowKey(catalogRow))
        }
        if (activeConflict) {
            activeConflictKeys.add(rowKey(catalogRow))
        }

        return { ...catalogRow, averageAmount, isActive }
    })

    return { rows, amountConflictKeys, activeConflictKeys }
}

// Regroupe les lignes ayant un site (exclut les lignes génériques marque-seule/globales).
export function groupByDealershipId(rows: InspectionVariableFeeTemplate[]): Map<number, InspectionVariableFeeTemplate[]> {
    return groupBy(rows.filter((row): row is InspectionVariableFeeTemplate & { dealership_id: number } => row.dealership_id !== null), row => row.dealership_id)
}

// Résumé utilisé pour l'aperçu : nombre de lignes réellement enregistrées et actives sur le total.
// `averageAmount` reste `null` tant qu'une ligne du catalogue par défaut n'a pas été sauvegardée
// (cf. `applyDataToRows`) : sans ce filtre, `isActive` retomberait sur la valeur par défaut du
// catalogue (souvent `true`, cf. constants.ts) pour des lignes jamais configurées par l'utilisateur.
export function buildSummary(rows: VariableFeeRow[]): FeeConfigSummary {
    return {
        rows,
        linesTotal: rows.length,
        linesConfigured: rows.filter(row => row.averageAmount !== null && row.isActive).length,
    }
}

/** Signature d'une config (pour fusionner les sites strictement identiques dans une même ligne). */
function signatureOf(rows: VariableFeeRow[]): string {
    return rows.map(row => `${row.averageAmount ?? 'null'}:${row.isActive}`).join(',')
}

// Pour l'aperçu : une ligne par marque, avec ses sites regroupés quand ils ont exactement la
// même config (même signature), + une ligne générique "-" si la marque a des frais sans site.
export function buildBrandConfigs(
    brandScopedData: InspectionVariableFeeTemplate[],
    siteNameById: Map<number, string>,
): BrandFeeConfig[] {
    const byBrand = groupBy(brandScopedData, row => row.brand as string)

    return Array.from(byBrand.entries()).flatMap(([ brand, rows ]) => {
        const genericRows = rows.filter(row => row.dealership_id === null)
        const bySite = groupByDealershipId(rows)

        const siteSummaries = Array.from(bySite.entries()).map(([ dealershipId, siteRows ]) => ({
            dealershipId,
            signature: signatureOf(applyDataToRows(getDefaultFeeRows(), siteRows)),
            value: buildSummary(applyDataToRows(getDefaultFeeRows(), siteRows)),
        }))

        const brandConfigs: BrandFeeConfig[] = buildSiteLabelGroups(siteSummaries, siteNameById)
            .map(({ siteLabel, dealershipIds, value }) => ({ brand, siteLabel, dealershipIds, ...value }))

        if (genericRows.length > 0) {
            brandConfigs.push({
                brand,
                siteLabel: '-',
                dealershipIds: [],
                ...buildSummary(applyDataToRows(getDefaultFeeRows(), genericRows)),
            })
        }

        return brandConfigs
    })
}
